import React, { useState, useEffect } from "react";
import Flashbar from "@amzn/awsui-components-console/flashbar";
import ChatbotPage from "./ChatbotPage";
import { BedrockAgentCoreClient, CANCELLATION_ERROR_MESSAGE } from "./BedrockAgentCoreClient";
import ChatSessionClient from "./ChatSessionClient";
import Constants from "../../mango/js/utils/Constants";
import HelperFunctions from "../HelperFunctions";

// Component type constants
export const COMPONENT_TYPE_SUGGESTIONS = "suggestions";

// Define suggestions that can be dynamically modified
const defaultSuggestions = [
    { text: `What is the lifecycle state of link ID amzn1.network.link.instance.684d2d08-0886-44d1-aec8-f51cd924e522?` },
    { text: "Return and summarize all orders that are part of project FBN NW25BP100033" },
    { text: "Return fiber spans for the path CMH051-ORD051-LF-01-Amazon-Zayo" }
];

const Chatbot = (props) => {
    // Extract suggestions from props, fallback to default if not provided
    const { initialSuggestions = defaultSuggestions } = props;
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [flashbarItems, setFlashbarItems] = useState([]);
    const [nestUser] = useState(!!HelperFunctions.userInPosixGroup(props.user, Constants.POSIX_GROUPS.NEST)
        || HelperFunctions.isLocalHost());

    // Session management state
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [loadingSessionId, setLoadingSessionId] = useState(null);
    const [showSessionList, setShowSessionList] = useState(false);

    // Pagination state
    const [hasMoreSessions, setHasMoreSessions] = useState(false);
    const [nextSessionToken, setNextSessionToken] = useState(null);
    const [isLoadingMoreSessions, setIsLoadingMoreSessions] = useState(false);

    // Create BedrockAgentCoreClient instance once - using useState to persist across renders
    const [bedrockClient] = useState(() => new BedrockAgentCoreClient(props.auth));

    // Create chatSessionClient singleton instance with auth from props
    const chatSessionClient = ChatSessionClient.getInstance(props.auth);

    const givenName = props.auth.getSignInUserSession().getIdToken().payload.given_name;
    const familyName = props.auth.getSignInUserSession().getIdToken().payload.family_name;

    // Create initial message with clickable suggestions
    const createInitialMessage = (suggestions = initialSuggestions) => ({
        role: "assistant",
        content: [
            {
                componentType: COMPONENT_TYPE_SUGGESTIONS,
                suggestions
            }
        ]
    });

    // Generate session ID when component loads
    useEffect(() => {
        if (!currentSessionId) {
            const newSessionId = chatSessionClient.generateSessionId();
            setCurrentSessionId(newSessionId);
        }
        // Set initial message with suggestions
        if (messages.length === 0) {
            setMessages([createInitialMessage(initialSuggestions)]);
        }
    }, [initialSuggestions]);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleInputChange = (value) => {
        setInputValue(value);
    };

    // Flashbar helper functions
    const addFlashbarItem = (type, header, content) => {
        const newItem = {
            type,
            dismissible: true,
            header,
            content,
            id: Date.now().toString()
        };
        setFlashbarItems(prevItems => [...prevItems, newItem]);
    };

    const removeFlashbarItem = (itemId) => {
        setFlashbarItems(prevItems => prevItems.filter(item => item.id !== itemId));
    };

    // Session management functions
    const loadSessions = async (loadMore = false) => {
        // Prevent loading if already loading or no more sessions available for load more
        if (loadMore && (!hasMoreSessions || isLoadingMoreSessions)) return;

        const isLoadingMore = loadMore;
        const isInitialLoad = !loadMore;

        if (isLoadingMore) {
            setIsLoadingMoreSessions(true);
        } else {
            setIsLoadingSessions(true);
        }

        try {
            const tokenToUse = isInitialLoad ? null : nextSessionToken;
            const result = await chatSessionClient.listSessions(props.auth.userId, 20, tokenToUse);

            // Create placeholder sessions immediately so they appear in the UI
            const placeholderSessions = result.sessions.map(session => ({
                ...session,
                firstMessage: "Loading..."
            }));

            // Add placeholder sessions to UI immediately
            if (isInitialLoad) {
                setSessions(placeholderSessions);
            } else {
                setSessions(prevSessions => [...prevSessions, ...placeholderSessions]);
            }

            // Update pagination state immediately
            setHasMoreSessions(result.hasMore);
            setNextSessionToken(result.nextToken);

            // Now load metadata for each session asynchronously and update them as they complete
            result.sessions.forEach(async (session) => {
                try {
                    const metadata = await chatSessionClient.getSessionMetadata(
                        props.auth.userId, session.sessionId
                    );
                    const sessionWithMetadata = {
                        ...session,
                        ...metadata,
                        isLoading: false
                    };

                    // Update this specific session in the sessions array
                    setSessions((prevSessions) => {
                        const updatedSessions = [...prevSessions];
                        const sessionIndex = updatedSessions.findIndex(s => s.sessionId === session.sessionId);
                        if (sessionIndex !== -1) {
                            updatedSessions[sessionIndex] = sessionWithMetadata;
                        }
                        return updatedSessions;
                    });
                } catch (error) {
                    console.warn(`Failed to load metadata for session ${session.sessionId}:`, error);
                    const sessionWithError = {
                        ...session,
                        firstMessage: "Error loading session"
                    };

                    // Update this specific session with error state
                    setSessions((prevSessions) => {
                        const updatedSessions = [...prevSessions];
                        const sessionIndex = updatedSessions.findIndex(s => s.sessionId === session.sessionId);
                        if (sessionIndex !== -1) {
                            updatedSessions[sessionIndex] = sessionWithError;
                        }
                        return updatedSessions;
                    });
                }
            });
        } catch (error) {
            console.error("Error loading sessions:", error);
            addFlashbarItem(
                "error",
                "Session Load Error",
                `Failed to load chat sessions: ${error.message}`
            );
        } finally {
            if (isLoadingMore) {
                setIsLoadingMoreSessions(false);
            } else {
                setIsLoadingSessions(false);
            }
        }
    };

    const loadMoreSessions = () => loadSessions(true);

    const createNewSession = () => {
        const newSessionId = chatSessionClient.generateSessionId();
        setCurrentSessionId(newSessionId);
        setMessages([createInitialMessage(initialSuggestions)]);
        setShowSessionList(false);
    };

    const selectSession = async (sessionId, userId = props.auth.userId) => {
        try {
            setIsLoadingSession(true);
            setLoadingSessionId(sessionId);
            const sessionMessages = await chatSessionClient.loadSession(userId, sessionId);
            setCurrentSessionId(sessionId);
            setMessages(sessionMessages);
            setShowSessionList(false);
        } catch (error) {
            console.error("Error loading session:", error);
            addFlashbarItem(
                "error",
                "Session Load Error",
                `Failed to load session: ${error.message}`
            );
            // Close the modal so user can see the error message
            setShowSessionList(false);
        } finally {
            setIsLoadingSession(false);
            setLoadingSessionId(null);
        }
    };

    const toggleSessionList = () => {
        if (!showSessionList) {
            loadSessions(false); // Initial load
        }
        setShowSessionList(!showSessionList);
    };

    const handleSendMessage = async (messageText = null) => {
        // Use provided messageText or fallback to inputValue
        const textToSend = messageText || inputValue;

        if (!textToSend.trim()) return;

        // Create new session if none exists
        if (!currentSessionId) {
            createNewSession();
        }

        // Add user message
        const userMessage = {
            role: "user",
            content: [
                {
                    text: textToSend
                }
            ]
        };

        // Clear input only if using inputValue (not for suggestion clicks)
        if (!messageText) {
            setInputValue("");
        }
        setMessages(prevMessages => [...prevMessages, userMessage]);
        setIsLoading(true);

        try {
            await bedrockClient.invokeAgentStream(
                textToSend,
                (chunk) => {
                    console.log("Received chunk:", chunk);

                    const assistantMessage = {
                        role: "assistant",
                        content: chunk.content
                    };

                    setMessages(prevMessages => [...prevMessages, assistantMessage]);
                },
                currentSessionId
            );
        } catch (error) {
            console.error("Error sending message:", error);

            // Handle cancellation differently from other errors
            if (error.message === CANCELLATION_ERROR_MESSAGE) {
                // Add cancelled response (no flashbar notification needed)
                const cancelledResponse = {
                    role: "assistant",
                    content: [{ text: CANCELLATION_ERROR_MESSAGE }]
                };

                setMessages(prevMessages => [...prevMessages, cancelledResponse]);
            } else {
                // Add flashbar error message for other errors
                addFlashbarItem(
                    "error",
                    "Chat Error",
                    `Failed to send message: ${error.message || "An unexpected error occurred. Please try again."}`
                );

                // Add error response
                const errorResponse = {
                    role: "assistant",
                    content: [{ text: "Sorry, I encountered an error processing your request. Please try again." }]
                };

                setMessages(prevMessages => [...prevMessages, errorResponse]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelRequest = () => {
        if (isLoading) {
            bedrockClient.cancelCurrentRequest();
        }
    };

    // Handle suggestion click - inject text and submit using handleSendMessage
    const handleSuggestionClick = async (suggestionText) => {
        // Use handleSendMessage with the suggestion text directly
        await handleSendMessage(suggestionText);
    };

    /**
     * This function handles prompt input action from users. If a message is not currently being
     * processed, this will send the message. If the message is in progress and the action is selected, the
     * session will be cancelled (from the client side).
     * @returns {Promise<void>}
     */
    const handlePromptInputAction = async () => {
        if (isLoading) {
            bedrockClient.cancelCurrentRequest();
        } else {
            await handleSendMessage();
        }
    };

    return (
        <div>
            <Flashbar
                items={flashbarItems.map(item => ({
                    ...item,
                    onDismiss: () => removeFlashbarItem(item.id)
                }))}
            />
            <ChatbotPage
                messages={messages}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                sendMessage={handleSendMessage}
                handlePromptInputAction={handlePromptInputAction}
                isOpen={isOpen}
                onToggle={handleToggle}
                familyName={familyName}
                givenName={givenName}
                isLoading={isLoading}
                // Suggestion click handler
                onSuggestionClick={handleSuggestionClick}
                // Request cancellation props
                onCancelRequest={handleCancelRequest}
                // Session management props
                currentSessionId={currentSessionId}
                sessions={sessions}
                isLoadingSessions={isLoadingSessions}
                isLoadingSession={isLoadingSession}
                loadingSessionId={loadingSessionId}
                showSessionList={showSessionList}
                onCreateNewSession={createNewSession}
                onSelectSession={selectSession}
                onToggleSessionList={toggleSessionList}
                // Pagination props
                hasMoreSessions={hasMoreSessions}
                isLoadingMoreSessions={isLoadingMoreSessions}
                onLoadMoreSessions={loadMoreSessions}
                userId={props.auth.userId}
                nestUser={nestUser}
            />
        </div>
    );
};

// Static function to create search suggestion
Chatbot.createSearchSuggestion = text => ({
    text
});

export default Chatbot;
